/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react'
import { DateTimePicker } from '@syncfusion/ej2-react-calendars'
import Lottie from 'lottie-react'

// Import animations
import cartAnim from './Animations/cart.json'
import confirmedAnim from './Animations/confirmed.json'
import loadAnim from './Animations/loading.json'
import onHoldAnim from './Animations/onHold.json'
import pickPackAnim from './Animations/pickPack.json'
import toBeConsAnim from './Animations/toBeCons.json'

// Mapping: Status → Lottie, background, border, size
const animationMap: Record<string, any> = {
  'Confirmed': confirmedAnim,
  'On hold': onHoldAnim,
  'Pick & Pack': pickPackAnim,
  'To be Cons.': toBeConsAnim,
}

const statusMap: Record<string, any> = {
  'Confirmed': ['RECEIVED', 'CONFIRMED'],
  'On hold': ['FAILED', 'EXCEPTION', 'BACKORDER', 'ONHOLD'],
  'Pick & Pack': ['OPEN'],
  'To be Cons.': ['TOBECONSIGNED', 'PARTIAL-CONSIGNED'],
}

const bgMap: Record<string, string> = {
  'Confirmed': 'bg-purple-100',
  'On hold': 'bg-orange-100',
  'Pick & Pack': 'bg-green-100',
  'To be Cons.': 'bg-pink-100',
}

const sizeMap: Record<string, { width: number; height: number }> = {
  'Confirmed': { width: 40, height: 32 },
  'On hold': { width: 40, height: 32 },
  'Pick & Pack': { width: 40, height: 32 },
  'To be Cons.': { width: 40, height: 32 },
}

export interface OrderStatusType {
  label: string
  value: number
}

export interface OrderStatusCardProps {
  openOrders: number
  statuses: OrderStatusType[]
  isLoading?: boolean
  filterVariables?: any
}

function formatDateSydney(date: Date): string {
  const sydneyDate = new Date(
    date.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }),
  )

  const pad = (n: number) => String(n).padStart(2, '0')
  return `${sydneyDate.getFullYear()}-${pad(sydneyDate.getMonth() + 1)}-${pad(
    sydneyDate.getDate(),
  )} ${pad(sydneyDate.getHours())}:${pad(sydneyDate.getMinutes())}:${pad(
    sydneyDate.getSeconds(),
  )}`
}

export const OrderStatusCard: React.FC<OrderStatusCardProps> = ({
  openOrders,
  statuses,
  isLoading,
  filterVariables,
}) => {
  return (
    <div className='bg-white rounded-xl shadow-md p-4 mb-0 flex flex-col md:flex-row items-start md:items-center gap-5 max-w-3xl w-full'>
      {/* Left: Open Orders */}
      <div
        onClick={() => {
          const queryParams = new URLSearchParams({
            orderTypes: (filterVariables?.orderTypes || []).join(','),
            partnerCodes: (filterVariables?.partnerCodes || []).join(','),
            orderStatus: [
              'RECEIVED',
              'CONFIRMED',
              'FAILED',
              'EXCEPTION',
              'OPEN',
              'ONHOLD',
              'TOBECONSIGNED',
              'PARTIAL-CONSIGNED',
              'BACKORDER',
            ].join(','),
            fromDate: '2025-01-01',
            toDate: formatDateSydney(new Date()), // formats as YYYY-MM-DD
          })

          window.open(
            `/customer-order/orders?${queryParams.toString()}`,
            '_blank',
          )
        }}
        className='flex flex-col items-center md:items-start md:w-40 w-full cursor-pointer hover:bg-P-300 rounded-lg transition-colors duration-200'>
        <div className='bg-orange-100 p-4 rounded-full flex items-center justify-center overflow-hidden w-[100px] h-[100px]'>
          <Lottie
            animationData={cartAnim}
            loop
            style={{ width: 80, height: 80 }}
          />
        </div>
        <p className='mt-2 text-sm text-N-600 font-semibold text-center md:text-left px-2'>
          Open Orders
        </p>
        <h2 className='text-xl font-semibold text-gray-600 text-center md:text-left p-5'>
          {openOrders}
        </h2>
      </div>

      {/* Divider (hide on mobile) */}
      <div className='hidden md:block h-[2px] w-10 bg-orange-300' />

      {/* Right: Statuses */}
      <div className='flex flex-col w-full'>
        {statuses.map(({ label, value }, index) => (
          <div key={label} className='flex flex-col py-2 w-full'>
            <div
              onClick={() => {
                const queryParams = new URLSearchParams({
                  orderTypes: (filterVariables?.orderTypes || []).join(','),
                  partnerCodes: (filterVariables?.partnerCodes || []).join(','),
                  orderStatus: statusMap[label]?.join(',') || '',
                  fromDate: '2025-01-01',
                  toDate: formatDateSydney(new Date()), // Formats to YYYY-MM-DD
                })

                window.open(
                  `/customer-order/orders?${queryParams.toString()}`,
                  '_blank',
                )
              }}
              className='flex items-center gap-3 px-2 cursor-pointer hover:bg-P-300 rounded-lg transition-colors duration-200'>
              <div
                className={`rounded-full ${
                  bgMap[label] || 'bg-orange-200'
                } p-2`}>
                <Lottie
                  animationData={animationMap[label]}
                  loop
                  style={{
                    width: sizeMap[label]?.width || 40,
                    height: sizeMap[label]?.height || 40,
                  }}
                />
              </div>
              <div>
                <p className='text-lg text-N-500 font-semibold'>{label}</p>
                {isLoading ? (
                  <Lottie
                    animationData={loadAnim}
                    loop
                    style={{ width: 100, height: 50 }}
                  />
                ) : (
                  <p className='text-base text-N-700 font-semibold'>{value}</p>
                )}
              </div>
            </div>
            {index !== statuses.length - 1 && (
              <div className='border-t-2 border-P-300 mt-2 ml-16' />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
